import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Anjaneya Inks & Coatings";
const DEFAULT_TITLE = "Anjaneya Inks & Coatings – Industrial Inks, Printing & Packaging Solutions";
const DEFAULT_DESC =
  "Anjaneya Inks & Coatings provides industrial inks, protective coatings, and specialty finishes for printing and packaging industries in India. Durable, high-quality solutions trusted by manufacturers.";

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    Object.entries(attrs.identity).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.values).forEach(([k, v]) => el.setAttribute(k, v));
};

/**
 * Per-page SEO: title, description, canonical, Open Graph / Twitter, robots.
 * Usage: <Seo title="..." description="..." />
 */
const Seo = ({ title, description, noindex = false, image }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title || DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const url = `${window.location.origin}${pathname}`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', { identity: { name: "description" }, values: { content: desc } });
    setMeta('meta[name="robots"]', { identity: { name: "robots" }, values: { content: noindex ? "noindex, nofollow" : "index, follow" } });
    setMeta('link[rel="canonical"]', { identity: { rel: "canonical" }, values: { href: url } });
    setMeta('meta[property="og:title"]', { identity: { property: "og:title" }, values: { content: fullTitle } });
    setMeta('meta[property="og:description"]', { identity: { property: "og:description" }, values: { content: desc } });
    setMeta('meta[property="og:url"]', { identity: { property: "og:url" }, values: { content: url } });
    setMeta('meta[property="og:site_name"]', { identity: { property: "og:site_name" }, values: { content: SITE_NAME } });
    setMeta('meta[property="og:type"]', { identity: { property: "og:type" }, values: { content: "website" } });
    if (image) {
      setMeta('meta[property="og:image"]', { identity: { property: "og:image" }, values: { content: image } });
    }
    setMeta('meta[name="twitter:title"]', { identity: { name: "twitter:title" }, values: { content: fullTitle } });
    setMeta('meta[name="twitter:description"]', { identity: { name: "twitter:description" }, values: { content: desc } });
  }, [title, description, noindex, image, pathname]);

  return null;
};

export const pageSeo = {
  home: {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
  },
  about: {
    title: "About Anjaneya Inks & Coatings – Ink & Coating Manufacturer in Medchal, Telangana",
    description:
      "Learn about Anjaneya Inks & Coatings, a Medchal, Telangana based manufacturer of printing inks and industrial coatings. Our mission, colour laboratory, production house and team behind consistent, long-lasting colour.",
  },
  production: {
    title: "Inks, Coatings & Shade Card – Offset, Flexo, Gravure Inks & Industrial Coatings | Anjaneya Inks & Coatings",
    description:
      "Explore our offset, flexographic and gravure inks, packaging varnishes, industrial coatings and custom colour matching. View the standard shade card, manufacturing process, delivery unit and ready stock for bulk and emergency orders.",
  },
  gallery: {
    title: "Gallery – Factory, Production House, Delivery Unit & Stock | Anjaneya Inks & Coatings",
    description:
      "Photo gallery of the Anjaneya Inks & Coatings facility in Medchal: main gate, company building, production house, branded delivery drums and ready stock of inks and coatings.",
  },
  contact: {
    title: "Contact Anjaneya Inks & Coatings – Enquiries, Quotes & Bulk Orders in Medchal, Telangana",
    description:
      "Contact Anjaneya Inks & Coatings for sales enquiries, technical questions, price quotes and bulk or emergency ink orders. Call +91 94908 53292 / +91 99669 63292 or visit us at Medchal, Telangana 501401.",
  },
  admin: {
    title: "Enquiry Dashboard | Anjaneya Inks & Coatings",
    description: "Private administration area.",
    noindex: true,
  },
};

export default Seo;
